/**
 * Stable React context instance for auth.
 * Keep this file free of api/firebase/i18n imports so Vite HMR does not recreate
 * the context while AuthProvider is still mounted (useAuth desync in dev).
 */
import { createContext } from 'react'
import type { RegisterUserInput, User } from './auth-types'

export type { RegisterUserInput, User } from './auth-types'

export interface AuthContextType {
    user: User | null
    isLoading: boolean
    isAuthenticated: boolean
    login: (identifier: string, password: string) => Promise<void>
    loginWithToken: (
        access_token: string,
        refresh_token?: string,
        firebase_token?: string,
    ) => Promise<void>
    loginWithGoogle: (returnUrl?: string) => Promise<void>
    loginWithFacebook: (returnUrl?: string) => Promise<void>
    register: (data: RegisterUserInput) => Promise<void>
    googleRegisterFirebase: (data: {
        id_token: string
        username: string
        registration_type: 'individual' | 'organization' | 'property_manager'
        user_type?: 'seller' | 'tenant'
        organization_name?: string
        organization_slug?: string
        phone?: string
        city: string
        preferred_language?: string
        billing_country_code: string
    }) => Promise<void>
    facebookRegisterFirebase: (data: {
        id_token: string
        username: string
        registration_type: 'individual' | 'organization' | 'property_manager'
        user_type?: 'seller' | 'tenant'
        organization_name?: string
        organization_slug?: string
        phone?: string
        city: string
        preferred_language?: string
        billing_country_code: string
    }) => Promise<void>
    logout: () => Promise<void>
    refreshUser: () => Promise<void>
    impersonateUser: (userId: string) => Promise<void>
    stopImpersonation: () => Promise<void>
    authToken: string | null
    activeRole: string | null
    setActiveRole: (role: string | null) => void
    changeLanguage: (lang: string) => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)
